import { FunctionLocationTracker, FunctionCallCache } from "@alphascriptsolutions/excel-core";
import { io } from "socket.io-client";

import { log } from "../logger";
import { AlphaAuditClient } from "./alphaAuditClient";
import { resolveAuditDecision } from "./rules";
import { AUDIT_HANDLERS } from "./handlers/index";
import { AuditContext } from "./types";
import { httpClient } from "../api/client";
import axios from "axios";
import { logger } from "@/helpers/tslog";

/**
 * ============================================================
 * Ribbon toggle
 * ============================================================
 */
export async function setAlphaAuditEnabled(enabled: boolean): Promise<void> {
  try {
    await Office.ribbon.requestUpdate({
      tabs: [
        {
          id: "AlphaScriptTab",
          groups: [
            {
              id: "BuildAndAudit",
              controls: [{ id: "AlphaAuditButton", enabled: !!enabled }],
            },
          ],
        },
      ],
    });
  } catch (e) {
    log.error("Failed to update AlphaAuditButton enabled state:", e);
  }
}

/**
 * ============================================================
 * Helpers
 * ============================================================
 */
async function recalcSelectedCell(): Promise<void> {
  await Excel.run(async (ctx) => {
    const r = ctx.workbook.getSelectedRange();
    r.calculate();
    await ctx.sync();
  });
}

function norm(s: string): string {
  return (s ?? "").trim().toLowerCase();
}

function keyPrefix(key: string): string {
  const i = key.indexOf(":");
  return norm(i === -1 ? key : key.slice(0, i));
}

/**
 * ---- address normalization (to fix A1 vs $A$1 mismatches) ----
 */
function parseRangeAddress(address: string): { sheetName: string; cellA1: string } | null {
  if (!address) return null;

  // Examples:
  //   Sheet1!$A$1
  //   'My Sheet'!A1
  const m = address.match(/^'?(.*?)'?!\$?([A-Za-z]{1,3})\$?(\d{1,7})$/);
  if (!m) return null;

  return { sheetName: m[1], cellA1: `${m[2]}${m[3]}` };
}

function withDollars(a1: string): string {
  const m = a1.match(/^([A-Za-z]{1,3})(\d{1,7})$/);
  return m ? `$${m[1]}$${m[2]}` : a1;
}

/**
 * ============================================================
 * Selection resolution
 * ============================================================
 *
 * This only answers:
 *  - is the selected cell a tracked function cell?
 *  - if yes, what is its context?
 *
 * It does NOT decide auditability.
 */
export type SelectedFunctionTarget =
  | { found: false; reason: string }
  | {
      found: true;
      ctx: AuditContext;
    };

export async function getSelectedFunctionTarget(): Promise<SelectedFunctionTarget> {
  const tracker = FunctionLocationTracker.getInstance();
  const calls = FunctionCallCache.getInstance();

  return Excel.run(async (ctx) => {
    const r = ctx.workbook.getSelectedRange();
    r.load(["rowCount", "columnCount", "address"]);
    await ctx.sync();

    if (r.rowCount !== 1 || r.columnCount !== 1) {
      return { found: false, reason: "Selection is not a single cell" };
    }

    const parsed = parseRangeAddress(r.address);
    if (!parsed) {
      return { found: false, reason: `Could not parse selection address: ${r.address}` };
    }

    const { sheetName, cellA1 } = parsed;

    // Try both address styles because tracker may store either.
    const addrCandidates = [cellA1, withDollars(cellA1)];

    let key: string | null = null;
    for (let i = 0; i < addrCandidates.length; i++) {
      const candidate = addrCandidates[i];
      const k = tracker.getFunctionKeyAt(sheetName, candidate);
      if (k) {
        key = k;
        break;
      }
    }

    if (!key) {
      return {
        found: false,
        reason: "Selected cell is not registered in FunctionLocationTracker",
      };
    }

    const meta = typeof (calls as any).getMetaByKey === "function" ? (calls as any).getMetaByKey(key) : null;

    const rawFunctionName = meta?.functionName && typeof meta.functionName === "string" ? meta.functionName : undefined;

    const resolvedFunctionName = rawFunctionName ? norm(rawFunctionName) : keyPrefix(key);

    return {
      found: true,
      ctx: {
        key,
        sheetName,
        address: cellA1,
        functionName: resolvedFunctionName,
        rawFunctionName,
        params: meta?.params ?? undefined,
        meta,
      },
    };
  });
}

/**
 * ============================================================
 * Selection evaluation + ribbon update
 * ============================================================
 */
async function evaluateSelectionAndUpdateAlphaAudit(): Promise<void> {
  try {
    const sel = await getSelectedFunctionTarget();

    if (!sel.found) {
      await setAlphaAuditEnabled(false);
      return;
    }

    const decision = resolveAuditDecision(sel.ctx);
    await setAlphaAuditEnabled(decision.auditable);
  } catch (e) {
    log.error("Selection evaluation failed:", e);
    await setAlphaAuditEnabled(false);
  }
}

/**
 * ============================================================
 * Workbook-wide wiring
 * ============================================================
 */
const wiredSheetIds = new Set<string>();

async function wireWorksheetSelectionChanged(worksheetId: string): Promise<void> {
  await Excel.run(async (ctx) => {
    const ws = ctx.workbook.worksheets.getItem(worksheetId);

    ws.onSelectionChanged.add(async () => {
      await evaluateSelectionAndUpdateAlphaAudit();
    });

    await ctx.sync();
    wiredSheetIds.add(worksheetId);
  });
}

async function wireAllWorksheets(): Promise<void> {
  const ids = await Excel.run(async (ctx) => {
    const sheets = ctx.workbook.worksheets;
    sheets.load("items/id");
    await ctx.sync();
    return sheets.items.map((s) => s.id);
  });

  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    if (!wiredSheetIds.has(id)) {
      // eslint-disable-next-line no-await-in-loop
      await wireWorksheetSelectionChanged(id);
    }
  }
}

let workbookEventsWired = false;

export async function initAlphaAudit(): Promise<void> {
  if (workbookEventsWired) return;

  log.info("init alpha audit websocket");

  await wireAllWorksheets();

  await Excel.run(async (ctx) => {
    const sheets = ctx.workbook.worksheets;

    sheets.onAdded.add(async (args: any) => {
      try {
        if (args?.worksheetId) await wireWorksheetSelectionChanged(args.worksheetId);
        else await wireAllWorksheets();
      } finally {
        await evaluateSelectionAndUpdateAlphaAudit();
      }
    });

    sheets.onDeleted.add(async (args: any) => {
      if (args?.worksheetId) wiredSheetIds.delete(args.worksheetId);
      await evaluateSelectionAndUpdateAlphaAudit();
    });

    const anySheets = sheets as any;
    if (anySheets.onActivated?.add) {
      anySheets.onActivated.add(async () => {
        await evaluateSelectionAndUpdateAlphaAudit();
      });
    }

    await ctx.sync();
  });

  await evaluateSelectionAndUpdateAlphaAudit();
  workbookEventsWired = true;
}

/**
 * ============================================================
 * Audit client + socket
 * ============================================================
 */
let auditClient: AlphaAuditClient | null = null;

async function getSocket() {
  return io(process.env.API_URL!);
}

/**
 * ============================================================
 * Download helpers
 * ============================================================
 */
function filenameFromUrl(url: string) {
  try {
    const u = new URL(url);
    const last = u.pathname.split("/").filter(Boolean).pop();
    return last ?? "download.xlsx";
  } catch {
    return "download.xlsx";
  }
}

function filenameFromContentDisposition(cd: string | null) {
  if (!cd) return null;

  // filename*=UTF-8''... (RFC 5987) or filename="..."
  const star = /filename\*\s*=\s*UTF-8''([^;\n]+)/i.exec(cd);
  if (star?.[1]) return decodeURIComponent(star[1].replace(/["']/g, ""));

  const plain = /filename\s*=\s*("?)([^";\n]+)\1/i.exec(cd);
  if (plain?.[2]) return plain[2];

  return null;
}

async function resolveToken(token?: string | (() => string | Promise<string>)): Promise<string | undefined> {
  if (!token) return undefined;
  if (typeof token === "string") return token;
  const t = await token();
  return t || undefined;
}

export async function downloadFileFromUrl(
  downloadUrl: string,
  opts?: {
    filename?: string;
  }
): Promise<void> {
  const { data, headers, status, statusText } = await httpClient.get(downloadUrl, {
    method: "GET",
    responseType: "blob",
  });

  if (status < 200 || status >= 300) {
    let body = "";

    try {
      body = await (data as Blob).text();
    } catch {
      body = "";
    }

    throw new Error(`Download failed (${status} ${statusText}): ${body}`);
  }

  const contentDisposition = headers["content-disposition"] ?? headers["Content-Disposition"];

  const filename = opts?.filename ?? filenameFromContentDisposition(contentDisposition) ?? filenameFromUrl(downloadUrl);

  const blob = data as Blob;
  const objectUrl = URL.createObjectURL(blob);

  try {
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function saveAsWithPicker(downloadUrl: string, suggestedName?: string): Promise<boolean> {
  // @ts-ignore
  if (!window.showSaveFilePicker) return false;

  let blob: Blob;

  try {
    const response = await httpClient.get(downloadUrl, {
      responseType: "blob",
    });

    blob = response.data as Blob;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(`Download failed: ${error.response.status} ${error.response.statusText}`);
    }

    throw error;
  }

  // @ts-ignore
  const handle = await window.showSaveFilePicker({
    suggestedName: suggestedName ?? "alpha-audit.xlsx",
    types: [
      {
        description: "Excel file",
        accept: {
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
        },
      },
    ],
  });

  const writable = await handle.createWritable();
  await writable.write(blob);
  await writable.close();

  return true;
}

async function downloadWithBestUX(downloadUrl: string, filename: string) {
  try {
    const saved = await saveAsWithPicker(downloadUrl, filename).catch(() => false);
    if (saved) return;

    await downloadFileFromUrl(downloadUrl, { filename });
  } catch (err) {
    logger.error(err);
  }
}

/**
 * ============================================================
 * Button click
 * ============================================================
 *
 * Flow:
 *  1) recalc selected cell
 *  2) resolve selected function target
 *  3) evaluate audit rule
 *  4) execute selected handler
 */
export async function onAlphaAuditClicked() {
  await recalcSelectedCell();

  const sel = await getSelectedFunctionTarget();
  if ("reason" in sel) {
    log.warn(`Alpha audit not available: ${sel.reason}`);
    return;
  }

  const decision = resolveAuditDecision(sel.ctx);
  if ("reason" in decision) {
    log.warn(`Alpha audit not available: ${decision.reason}`);
    return;
  }

  if (!auditClient) {
    auditClient = new AlphaAuditClient(getSocket);
  }

  const handler = AUDIT_HANDLERS[decision.handler];
  if (!handler) {
    log.error(`Alpha audit failed: missing handler "${decision.handler}"`, {
      functionName: decision.auditFunctionName,
      ctx: sel.ctx,
    });
    return;
  }

  try {
    const result = await handler(auditClient, decision, sel.ctx);

    if (result.kind === "download") {
      log.info("Alpha audit complete", {
        auditId: result.auditId,
        downloadUrl: result.downloadUrl,
        functionName: decision.auditFunctionName,
        handler: decision.handler,
      });

      await downloadWithBestUX(result.downloadUrl, result.filename ?? `excel_alpha_audit_${result.auditId}.xlsx`);

      return;
    }

    if (result.kind === "opened-url") {
      log.info("Alpha audit completed via URL open", {
        url: result.url,
        functionName: decision.auditFunctionName,
        handler: decision.handler,
        message: result.message,
      });

      return;
    }
  } catch (e) {
    log.error("Alpha audit failed", e);
  }
}
